/**
 * Displayed Test ID — see GLOSSARY.md → Trial Authoring → Displayed Test ID.
 *
 * The positional identifier a reviewer reads for a Test Case: the case's own
 * Test ID prefix + its 1-based position within its subsection's curated order,
 * zero-padded to two digits (widening past 99 without truncation). It is a pure
 * presentation projection — derived on read, never persisted, never an identity,
 * never a routing/audit/render-matching key (FR-006). It is intentionally
 * NON-UNIQUE across subsections (every subsection has a `…-01`). Distinct from
 * the persisted Test ID handle (the stable id) and from F-017's "display test
 * ID" (which is the handle): this is the renumbered display value layered on top.
 */
import { testIdPrefix } from './test-id.js';

/** `prefix(testId)` + the zero-padded 1-based position, e.g. `DO-04` @ 1 → `DO-01`. */
export const displayTestId = (testId: string, position1Based: number): string =>
  `${testIdPrefix(testId)}-${String(position1Based).padStart(2, '0')}`;

/**
 * Assign the Displayed Test ID to every case in ONE subsection. The position is
 * the case's 1-based rank when the cases are sorted ascending by their curated
 * `order` (a gap-tolerant sort key, not an index — so gaps are tolerated and the
 * `order` value is never used directly as the number). The input array order is
 * PRESERVED — this numbers, it does not reorder — so the caller's own ordering
 * (and any later view lens) is untouched. Pure and deterministic (FR-009).
 */
export const assignDisplayTestIds = <
  C extends { readonly id: string; readonly testId: string; readonly order: number },
>(
  cases: readonly C[],
): ReadonlyArray<C & { readonly displayTestId: string }> => {
  const orderedIds = [...cases].sort((a, b) => a.order - b.order).map((aCase) => aCase.id);
  return cases.map((aCase) => ({
    ...aCase,
    displayTestId: displayTestId(aCase.testId, orderedIds.indexOf(aCase.id) + 1),
  }));
};
